import { apiService } from '../../../../components/api/apiService';

export interface EnterpriseChatBotResourceMap {
  chatbot_id: string;
  agent_title: string;
  summary: ResourceMapSummary;
  copilot_details: CopilotDetails;
  health_metrics: HealthMetrics | null;
  copilot_url: string | null;
  knowledge_bases: ResourceMapKnowledgeBase[];
  intents: ResourceMapIntent[];
  access_management: AccessManagement;
}

export interface ResourceMapSummary {
  active_intents: number;
  knowledge_bases: number;
  access_grants: number;
  conversations_24h: number | null;
}

export interface CopilotDetails {
  agent_type: string;
  chat_type: string;
  llm_model: string | null;
  languages: string[];
  created_at: string | null;
  intents_total: number;
}

export interface HealthMetrics {
  success_rate: number | null;
  avg_response_ms: number | null;
  error_rate: number | null;
  active_sessions: number | null;
}

export interface ResourceMapKnowledgeBase {
  id: string;
  name: string;
  slug: string | null;
  description: string | null;
  document_count: number;
  knowledge_workspace_id: string;
}

export interface ResourceMapIntent {
  id: string;
  name: string | null;
  code: string | null;
  status: string | null;
  category: string;
  intent_type_label: string | null;
  confidence_threshold: number | null;
  description: string | null;
  resources: ResourceMapLinkedResource[];
  access: IntentAccess;
}

export interface ResourceMapLinkedResource {
  id: string | null;
  name: string;
  description: string | null;
  document_count: number | null;
  resource_type: 'knowledge_base' | 'orchestrator' | 'data_model' | string;
  knowledge_workspace_id?: string | null;
}

export interface IntentAccess {
  departments: AccessDepartment[];
  users: AccessUser[];
  exclusions: AccessExclusion[];
}

export interface AccessManagement {
  departments: AccessDepartment[];
  users: AccessUser[];
  roles: AccessRole[];
  exclusions: AccessExclusion[];
}

export interface AccessDepartment {
  id: string;
  name: string;
  member_count: number;
}

export interface AccessUser {
  id: string;
  name: string;
  email: string;
  department: string | null;
}

export interface AccessRole {
  id: string;
  name: string;
  user_count: number;
}

export interface AccessExclusion {
  id: string;
  name: string;
  email: string | null;
  note: string | null;
}

export const isActiveIntent = (status: string | null): boolean =>
  status?.toLowerCase() === 'active';

export const formatConfidence = (threshold: number | null): string =>
  threshold != null ? `${Math.round(threshold * 100)}%` : '—';

export const categoryColor: Record<string, string> = {
  'Knowledge Base': 'blue',
  Action: 'purple',
  Report: 'orange',
};

export function formatNullable(value: string | number | null | undefined, fallback = '—'): string {
  if (value == null || value === '') return fallback;
  return String(value);
}

export function formatConversations24h(value: number | null): string {
  if (value == null) return 'N/A';
  return value.toLocaleString();
}

export function formatResourceSubtitle(resource: ResourceMapLinkedResource): string | undefined {
  if (resource.resource_type === 'knowledge_base') {
    return resource.document_count != null
      ? `${resource.document_count} documents`
      : undefined;
  }
  return resource.description ?? undefined;
}

export function knowledgeBaseHref(
  knowledgeWorkspaceId: string | null | undefined,
  kbId?: string | null | undefined,
): string {
  if (!knowledgeWorkspaceId) return '/portal/knowledge-base';
  const workspaceSegment = encodeURIComponent(knowledgeWorkspaceId);
  if (kbId) {
    return `/portal/knowledge-base/${workspaceSegment}/${encodeURIComponent(kbId)}`;
  }
  return `/portal/knowledge-base/${workspaceSegment}`;
}

export function resourceHref(resource: ResourceMapLinkedResource): string {
  switch (resource.resource_type) {
    case 'knowledge_base':
      return knowledgeBaseHref(resource.knowledge_workspace_id, resource.id);
    case 'orchestrator':
      return resource.id ? `/portal/flowbuilder/${encodeURIComponent(resource.id)}` : '/portal/flowbuilder';
    case 'data_model':
      return '/portal/mcpresources';
    default:
      return '/portal/mcpresources';
  }
}

export async function fetchChatBotResourceMap(chatbotId: string): Promise<EnterpriseChatBotResourceMap> {
  try {
    return await apiService.get(
      `/api/enterprisechatbot/${encodeURIComponent(chatbotId)}/resourcemap`,
      {},
      'backend',
    );
  } catch (error: unknown) {
    const axiosError = error as { response?: { status?: number; data?: { message?: string } } };
    const status = axiosError.response?.status;
    const message = axiosError.response?.data?.message;

    if (status === 404) {
      const notFound = new Error(message ?? `Chatbot '${chatbotId}' not found.`);
      (notFound as Error & { status: number }).status = 404;
      throw notFound;
    }

    throw new Error(message ?? `Failed to load resource map (${status ?? 'unknown'})`);
  }
}
